import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { CalendarClock, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { useOpmaak } from '../professionalDashboard/opmaak';
import {
  tariefContractjaarApi,
  TARIEF_CONTRACTJAAR_KEY,
  type TariefKeuze,
  type TariefVoorstel,
} from '../../services/tariefContractjaarApi';

const euro = (n: number | null | undefined) =>
  new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(Number(n ?? 0));

const KNOP_BASIS = 'inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const KNOP = {
  primair: `${KNOP_BASIS} bg-navy-900 text-white hover:bg-navy-800`,
  secundair: `${KNOP_BASIS} border border-gray-300 bg-white text-navy-900 font-medium hover:border-gray-400`,
};

/**
 * Indexatieslot (besluit 4, migratie 196): 30 dagen voor het einde van het
 * contractjaar kiest de professional per samenwerking: tarief handhaven,
 * inflatie-indexatie of een eigen voorstel. Verborgen als er niets loopt.
 */
export const TariefContractjaarKaart: React.FC = () => {
  const { data } = useQuery({
    queryKey: TARIEF_CONTRACTJAAR_KEY,
    queryFn: tariefContractjaarApi.professional,
    staleTime: 60_000,
  });
  const voorstellen = data?.voorstellen ?? [];
  if (voorstellen.length === 0) return null;
  return (
    <div className="space-y-3">
      {voorstellen.map((v) => (
        <VoorstelKaart key={v.id} voorstel={v} indexatiePct={data?.indexatie_pct ?? null} indexatieBron={data?.indexatie_bron ?? null} />
      ))}
    </div>
  );
};

const VoorstelKaart: React.FC<{ voorstel: TariefVoorstel; indexatiePct: number | null; indexatieBron: string | null }> = ({
  voorstel: v,
  indexatiePct,
  indexatieBron,
}) => {
  const { t } = useTranslation('tariefContractjaar');
  const opmaak = useOpmaak();
  const queryClient = useQueryClient();
  const kanKiezen = ['GEMELD', 'INGEDIEND', 'GEHANDHAAFD', 'AFGEWEZEN'].includes(v.status);
  const [open, setOpen] = useState(v.status === 'GEMELD' || v.status === 'AFGEWEZEN');
  const [keuze, setKeuze] = useState<TariefKeuze>(v.keuze ?? 'HANDHAVEN');
  const [tarief, setTarief] = useState<string>(v.keuze === 'EIGEN_VOORSTEL' && v.voorgesteld_tarief ? String(v.voorgesteld_tarief) : '');
  const [toelichting, setToelichting] = useState(v.keuze === 'EIGEN_VOORSTEL' ? v.toelichting ?? '' : '');

  // Inflatie-indexatie volgt de jaarmutatie van de CBS-CPI alle huishoudens (besluit 30-10).
  const indexatieMogelijk = indexatiePct != null && indexatiePct > 0;
  const indexatieTarief = indexatieMogelijk ? Math.round(v.huidig_tarief * (1 + indexatiePct / 100) * 100) / 100 : null;

  const kies = useMutation({
    mutationFn: () =>
      tariefContractjaarApi.kies(v.id, {
        keuze,
        tarief: keuze === 'EIGEN_VOORSTEL' ? parseFloat(tarief.replace(',', '.')) : null,
        toelichting: keuze === 'EIGEN_VOORSTEL' ? toelichting.trim() : null,
      }),
    onSuccess: (r) => {
      toast.success(r.status === 'GEHANDHAAFD' ? t('professional.gehandhaafd') : t('professional.ingediend'));
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: TARIEF_CONTRACTJAAR_KEY });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || t('fout')),
  });

  const status = () => {
    switch (v.status) {
      case 'INGEDIEND':
        return { icoon: Clock, kleur: 'text-amber-600', tekst: t('professional.status.INGEDIEND', { tarief: euro(v.voorgesteld_tarief) }) };
      case 'GEHANDHAAFD':
        return { icoon: CheckCircle2, kleur: 'text-green-600', tekst: t('professional.status.GEHANDHAAFD', { tarief: euro(v.huidig_tarief) }) };
      case 'GOEDGEKEURD':
        return { icoon: CheckCircle2, kleur: 'text-green-600', tekst: t('professional.status.GOEDGEKEURD', { tarief: euro(v.voorgesteld_tarief), datum: opmaak.dag(v.contractjaar_eind) }) };
      case 'AFGEWEZEN':
        return { icoon: XCircle, kleur: 'text-red-600', tekst: t('professional.status.AFGEWEZEN', { reden: v.afwijs_reden ?? '' }) };
      default:
        return null;
    }
  };
  const s = status();

  const optie = (waarde: TariefKeuze, label: string, uitleg: string, uit = false) => (
    <label className={`flex items-start gap-3 rounded-lg border p-3 ${keuze === waarde ? 'border-navy-700 bg-navy-50/40' : 'border-gray-200'} ${uit ? 'opacity-50' : 'cursor-pointer'}`}>
      <input type="radio" name={`keuze-${v.id}`} className="mt-1" disabled={uit} checked={keuze === waarde} onChange={() => setKeuze(waarde)} />
      <span>
        <span className="block text-sm font-semibold text-navy-900">{label}</span>
        <span className="block text-xs text-navy-600">{uitleg}</span>
      </span>
    </label>
  );

  return (
    <section className="rounded-xl border border-gray-200 border-l-4 border-l-amber-400 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <CalendarClock className="mt-0.5 h-6 w-6 flex-shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1 space-y-2">
          <h2 className="text-sm font-semibold text-navy-900">{t('professional.titel', { organisatie: v.organisatie })}</h2>
          <p className="text-sm text-navy-700">
            {t('professional.tekst', { datum: opmaak.dag(v.contractjaar_eind), dagen: v.dagen_resterend, tarief: euro(v.huidig_tarief) })}
          </p>
          {s && (
            <p className="flex items-center gap-1.5 text-sm text-navy-800">
              <s.icoon className={`h-4 w-4 ${s.kleur}`} />
              {s.tekst}
            </p>
          )}

          {kanKiezen && !open && (
            <button type="button" className={KNOP.secundair} onClick={() => setOpen(true)}>
              {t('professional.wijzigKeuze')}
            </button>
          )}

          {kanKiezen && open && (
            <div className="space-y-2 pt-1">
              {optie('HANDHAVEN', t('professional.optie.HANDHAVEN'), t('professional.uitleg.HANDHAVEN', { tarief: euro(v.huidig_tarief) }))}
              {optie(
                'INDEXATIE',
                t('professional.optie.INDEXATIE'),
                indexatieMogelijk
                  ? t('professional.uitleg.INDEXATIE', { pct: indexatiePct, tarief: euro(indexatieTarief), bron: indexatieBron ?? '' })
                  : indexatiePct != null
                    ? t('professional.uitleg.INDEXATIE_NIET_POSITIEF', { pct: indexatiePct, bron: indexatieBron ?? '' })
                    : t('professional.uitleg.INDEXATIE_ONBEKEND'),
                !indexatieMogelijk
              )}
              {optie('EIGEN_VOORSTEL', t('professional.optie.EIGEN_VOORSTEL'), t('professional.uitleg.EIGEN_VOORSTEL'))}

              {keuze === 'EIGEN_VOORSTEL' && (
                <div className="grid gap-2 sm:grid-cols-[10rem_1fr]">
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    inputMode="decimal"
                    value={tarief}
                    onChange={(e) => setTarief(e.target.value)}
                    placeholder={t('professional.tariefPlaceholder')}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                  <textarea
                    value={toelichting}
                    onChange={(e) => setToelichting(e.target.value)}
                    rows={2}
                    maxLength={2000}
                    placeholder={t('professional.toelichtingPlaceholder')}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              )}

              <p className="text-xs text-navy-500">{t('professional.voetnoot')}</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className={KNOP.primair}
                  disabled={kies.isPending || (keuze === 'EIGEN_VOORSTEL' && (!tarief || !toelichting.trim()))}
                  onClick={() => kies.mutate()}
                >
                  {keuze === 'HANDHAVEN' ? t('professional.bevestigHandhaven') : t('professional.indienen')}
                </button>
                {v.status !== 'GEMELD' && (
                  <button type="button" className={KNOP.secundair} onClick={() => setOpen(false)}>
                    {t('annuleren')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
